/**
 * Putting the chosen theme on the page.
 *
 * The rule for which theme to paint lives in `packages/core/src/theme.ts`; this
 * is the part that touches the DOM and `localStorage`, and it is deliberately
 * thin.
 *
 * Two things are load-bearing:
 *
 *  1. **"Match my phone" removes the attribute rather than writing one.** The
 *     stylesheet answers the system preference itself, through
 *     `prefers-color-scheme`. Writing `data-theme="light"` for "system" would
 *     freeze the app light on a phone that goes dark at sunset.
 *  2. **A customer's page is pinned light.** `/p/<token>`, `/c/<token>`,
 *     `/s/<token>` and the colour pages (`isOutsiderPath` below) are read by
 *     somebody who never chose anything and may well be on a phone set to
 *     dark. `data-theme="light"` there beats the media query, so the
 *     contract, the change order, the storm report and the colours look the
 *     same on every screen.
 */

import {
  colorSlugFromPath,
  DEFAULT_SCENT,
  SCENT_STORAGE_KEY,
  THEME_STORAGE_KEY,
  readScentChoice,
  readThemeChoice,
  type ScentId,
  type ThemeChoice,
} from "@roof-hawk/core";

/** Read the remembered choice. A browser that refuses storage gets "system". */
export function storedThemeChoice(): ThemeChoice {
  try {
    return readThemeChoice(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

/** Paint `choice`, and remember it. */
export function applyThemeChoice(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);

  try {
    if (choice === "system") window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Private browsing, or storage turned off. The theme still applies for as
    // long as the page is open, which is the useful half.
  }
}

/** Read the remembered scent. A browser that refuses storage gets the company look. */
export function storedScent(): ScentId {
  try {
    return readScentChoice(window.localStorage.getItem(SCENT_STORAGE_KEY));
  } catch {
    return DEFAULT_SCENT;
  }
}

/**
 * Paint `scent`, and remember it.
 *
 * The company look removes the attribute rather than writing one, so a rep
 * who has never picked and a rep who picked "Roof Board" are the same thing:
 * the stylesheet's own colours, nothing stored.
 */
export function applyScent(scent: ScentId): void {
  const root = document.documentElement;
  if (scent === DEFAULT_SCENT) root.removeAttribute("data-scent");
  else root.setAttribute("data-scent", scent);

  try {
    if (scent === DEFAULT_SCENT)
      window.localStorage.removeItem(SCENT_STORAGE_KEY);
    else window.localStorage.setItem(SCENT_STORAGE_KEY, scent);
  } catch {
    // Storage refused. The colours still apply while the page is open.
  }
}

/**
 * Hold the page light, in the company's colours, whatever the reader's phone
 * prefers and whatever the rep who sent it has picked for their own screen.
 *
 * Used by the customer's document, the storm report and the colour pages, and
 * by nothing else. It writes no preference: this is about the page, not about
 * the person.
 */
export function pinLightTheme(): void {
  document.documentElement.setAttribute("data-theme", "light");
  document.documentElement.removeAttribute("data-scent");
}

/**
 * Is this URL one a customer or an adjuster was sent, rather than staff?
 *
 * A proposal (`/p`), a change order (`/c`), a storm report (`/s`), and since
 * 2026-09-30 a colour page (`/colors`, `/colors/<slug>`), which is drawn in
 * the proposal's palette. The change order joined on 2026-10-04: it is signed
 * on the rep's own phone as often as on the customer's, and it is a contract.
 */
export const isOutsiderPath = (pathname: string): boolean =>
  /^\/[pcs]\/[0-9a-f]{32}\/?$/.test(pathname) ||
  colorSlugFromPath(pathname) !== null;

/**
 * Settle the theme before the first paint.
 *
 * Called from main.tsx rather than from a component, because a theme applied
 * after React has rendered is a white flash on a dark phone — the one thing
 * the dark theme exists to prevent.
 */
export function settleTheme(pathname: string): void {
  if (isOutsiderPath(pathname)) {
    pinLightTheme();
    return;
  }
  applyThemeChoice(storedThemeChoice());
  applyScent(storedScent());
}
